import React, { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { Checkbox, cn } from '@duck/ui'
import { toast } from 'sonner'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import {
  getPersonStatusColorClass,
  getDisplayedStatusDate,
  formatShortNumericDate,
} from '@/hooks/TableSettings/peopleTable.utils'
import { PersonAssignmentsRoster } from './PersonAssignmentsRoster'
import { PersonDetailsPanel } from './PersonDetailsPanel'
import type { ResultsDetails } from '../assignments/SubmissionResultsDialog'

export interface PersonRowModel {
  person: UserService.Person
  totalAssignments: number
  submittedAssignments: number
  bestGrade?: number
  details: PartnerService.AssignmentDetails[]
  /** Linked user account's createdAt — the real "joined" date. Absent for added/invited people. */
  joinedAt?: string
}

interface PersonRowProps {
  model: PersonRowModel
  checked: boolean
  onToggle: (checked: boolean) => void
  getAssignmentTaskName: (assignmentId: string) => string
  isTaskLoading: boolean
  onViewResults: (d: ResultsDetails) => void
}

// Status word + its date: real join date (user.createdAt) for joined members, else the person
// proxy (createdAt for added, updatedAt for invited). Date shows only when it exists.
const StatusLabel: React.FC<{ person: UserService.Person; joinedAt?: string }> = ({ person, joinedAt }) => {
  const { t } = useTar()
  const word = t(`partner.onboarding_${person.onboardingStatus}` as TranslationKey)
  const date = formatShortNumericDate(getDisplayedStatusDate(person, joinedAt))
  return (
    <span className="text-xs">
      <span className={cn('font-medium', getPersonStatusColorClass(person.onboardingStatus))}>{word}</span>
      {date && (
        <>
          <span className="text-muted-foreground">: </span>
          <span className="text-muted-foreground" dir="ltr">{date}</span>
        </>
      )}
    </span>
  )
}

export const PersonRow: React.FC<PersonRowProps> = ({
  model, checked, onToggle, getAssignmentTaskName, isTaskLoading, onViewResults,
}) => {
  const { t } = useTar()
  const { person, totalAssignments, submittedAssignments, bestGrade, details, joinedAt } = model
  const [open, setOpen] = useState(false)
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email
  const initials = `${person.firstName?.charAt(0) || ''}${person.lastName?.charAt(0) || ''}`.toUpperCase()
  const pct = totalAssignments > 0 ? (submittedAssignments / totalAssignments) * 100 : 0

  const copyInvite = async () => {
    if (!person.invitationToken) return
    const link = `${window.location.origin}/?token=${person.invitationToken}`
    try { await navigator.clipboard.writeText(link); toast.success(t('common.linkCopied')) }
    catch { toast.error(t('common.failedToCopyLink') + '\n' + link, { duration: 15000 }) }
  }

  const toggleOpen = () => setOpen((v) => !v)

  return (
    <div className="border-b border-border/60 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggleOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen() } }}
        className="grid min-h-[60px] cursor-pointer grid-cols-[26px_auto_1fr_auto] items-center gap-3 px-3.5 hover:bg-muted/30 lg:h-[60px] lg:grid-cols-[26px_auto_minmax(0,1.5fr)_180px_minmax(0,1fr)]"
      >
        <span onClick={(e) => e.stopPropagation()} className="flex items-center">
          <Checkbox aria-label={t('partner.selectPerson', { name })} checked={checked} onCheckedChange={(c) => onToggle(!!c)} className="h-4 w-4" />
        </span>
        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open ? 'rotate-90' : 'rtl:rotate-180')} />
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{initials}</span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-semibold text-foreground">{name}</span>
          </div>
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <StatusLabel person={person} joinedAt={joinedAt} />
          {person.onboardingStatus === 'invited' && person.invitationToken && (
            <button type="button" onClick={(e) => { e.stopPropagation(); copyInvite() }} className="text-xs text-primary hover:underline">{t('common.copyLink')}</button>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span aria-hidden="true" className="hidden h-1.5 w-14 overflow-hidden rounded-full bg-muted lg:block">
            <span className="block h-full bg-primary" style={{ width: `${pct}%` }} />
          </span>
          <span className="hidden lg:inline">{submittedAssignments}/{totalAssignments}</span>
          {bestGrade !== undefined ? (
            <GradeBadge grade={bestGrade} small />
          ) : (
            <>
              {/* Desktop keeps a plain placeholder — the dedicated Status column already carries the label. */}
              <span className="hidden text-muted-foreground/70 lg:inline">{t('partner.noGrade')}</span>
              {/* Mobile has no Status column, so an ungraded row surfaces the status label here instead. */}
              <span className="lg:hidden"><StatusLabel person={person} joinedAt={joinedAt} /></span>
            </>
          )}
        </div>
      </div>
      {open && (
        // The expanded body reads as a single recessed surface folded out of the row:
        // inset shadows on both edges (instead of hard rules) sink it below the header/next
        // row, and the unified dark tint groups bio + roster as one unit that stays legible
        // when several are open.
        <div className="bg-background/40 shadow-recess">
          <PersonDetailsPanel person={person} joinedAt={joinedAt} />
          <PersonAssignmentsRoster
            person={person}
            details={details}
            getAssignmentTaskName={getAssignmentTaskName}
            isTaskLoading={isTaskLoading}
            onViewResults={onViewResults}
          />
        </div>
      )}
    </div>
  )
}
